import { Check } from "lucide-react";
import { Link } from "react-router-dom";

export interface ReadinessStep {
  key: string;
  label: string;
  done: boolean;
  /** Where the owner goes to finish this. Omitted when it's done here. */
  href?: string;
}

interface PageReadinessStripProps {
  steps: ReadinessStep[];
}

/**
 * What makes a Genie Page good, and how much of it this Genie has.
 *
 * Every step links to the surface that actually owns it — the knowledge base
 * publishes guides, the Genie's Resources card holds manuals. This strip
 * reports and routes; it does not duplicate any of them.
 */
export const PageReadinessStrip = ({ steps }: PageReadinessStripProps) => {
  const done = steps.filter((step) => step.done).length;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5">
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <h3 className="text-base font-semibold text-gray-900">Page readiness</h3>
        <span className="text-sm text-gray-500">
          {done} of {steps.length} done
        </span>
      </div>

      <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] transition-all"
          style={{ width: `${(done / steps.length) * 100}%` }}
        />
      </div>

      <ul className="grid gap-2 sm:grid-cols-2">
        {steps.map((step) => {
          const body = (
            <>
              <span
                className={`grid h-5 w-5 shrink-0 place-items-center rounded-full ${
                  step.done ? "bg-genie-primary text-white" : "border border-gray-300 bg-white"
                }`}
              >
                {step.done && <Check className="h-3 w-3" strokeWidth={3} />}
              </span>
              <span
                className={`text-[13.5px] ${step.done ? "text-gray-500 line-through" : "text-gray-800"}`}
              >
                {step.label}
              </span>
            </>
          );

          return (
            <li key={step.key}>
              {!step.done && step.href ? (
                <Link
                  to={step.href}
                  className="flex items-center gap-2.5 rounded-lg px-1 py-1 transition-colors hover:bg-gray-50"
                >
                  {body}
                </Link>
              ) : (
                <span className="flex items-center gap-2.5 px-1 py-1">{body}</span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PageReadinessStrip;
